import { Box, Container } from "@mantine/core";
import { useLocation } from "@reach/router";
import {
  IconBrain,
  IconBrush,
  IconCalculator,
  IconCodeDots,
  IconDatabase,
  IconDeviceMobile,
  IconHelp,
  IconMessage,
  IconSearch,
  IconSeedling,
  IconServer,
  IconShieldCheck,
  IconSparkles,
  IconTool,
  IconUsers,
} from "@tabler/icons-react";
import React, { ReactNode, useEffect, useState } from "react";

import { BreakpointProvider } from "@contexts";

import {
  CookieBanner,
  Footer,
  Header,
  LastUpdated,
  MobileHeader,
} from "@components";

import { HeaderConfig, MenuStructure } from "@types";

interface LayoutProps {
  children: ReactNode;
  headerConfig?: HeaderConfig;
}

const menuStructure: MenuStructure = {
  items: [
    {
      label: "Home",
      href: "/",
    },
    {
      label: "About",
      href: "/about",
      submenu: {
        title: "About",
        content:
          "Want to know more about who we are? Learn everything from where we began, who we are, what guides our day-to-day, and answers to our most-asked questions.",
        cta: {
          label: "Learn more about us",
          href: "/about",
        },
        links: [
          {
            label: "Frequently Asked Questions",
            icon: <IconHelp size={24} />,
            href: "/about#faqs",
          },
          {
            label: "Pricing",
            icon: <IconCalculator size={24} />,
            href: "/about/pricing",
          },
        ],
      },
    },
    {
      label: "AI",
      href: "/ai",
      submenu: {
        title: "AI Services",
        content:
          "AI-augmented delivery, product integration, and consultancy from a team that uses AI every day — not as a pitch, but as practice.",
        cta: {
          label: "See all our AI services",
          href: "/ai",
        },
        links: [
          {
            label: "AI-Powered Delivery",
            icon: <IconBrain size={24} />,
            href: "/ai/ai-powered-delivery",
            headerProps: { light: true },
          },
          {
            label: "AI Product Integration",
            icon: <IconSparkles size={24} />,
            href: "/ai/ai-product-integration",
            headerProps: { light: true },
          },
          {
            label: "AI Consultancy",
            icon: <IconSeedling size={24} />,
            href: "/ai/ai-consultancy",
            headerProps: { light: true },
          },
        ],
      },
    },
    {
      label: "Services",
      href: "/services",
      submenu: {
        title: "Services",
        content:
          "Looking to build a new web site or web application? We specialize in building web sites that look professional, load quickly, and offer powerful functionality and interactivity to users.",
        cta: {
          label: "See all the services we offer",
          href: "/services",
        },
        links: [
          {
            label: "Web Design",
            icon: <IconBrush size={24} />,
            href: "/services/web-design",
            headerProps: { light: true },
          },
          {
            label: "Web Development",
            icon: <IconCodeDots size={24} />,
            href: "/services/web-development",
            headerProps: { light: true },
          },
          {
            label: "Search Engine Optimization",
            icon: <IconSearch size={24} />,
            href: "/services/seo",
            headerProps: { light: true },
          },
          {
            label: "DevOps",
            icon: <IconServer size={24} />,
            href: "/services/devops",
            headerProps: { light: true },
          },
          {
            label: "Enterprise Software",
            icon: <IconDatabase size={24} />,
            href: "/services/enterprise-software",
            headerProps: { light: true },
          },
          {
            label: "CMS Solutions",
            icon: <IconTool size={24} />,
            href: "/services/cms",
            headerProps: { light: true },
          },
          {
            label: "Custom Apps",
            icon: <IconDeviceMobile size={24} />,
            href: "/services/custom-apps",
            headerProps: { light: true },
          },
          {
            label: "Agency Partnerships",
            icon: <IconUsers size={24} />,
            href: "/services/agency-partnerships",
            headerProps: { light: true },
          },
          {
            label: "Interactive Experiences",
            icon: <IconSparkles size={24} />,
            href: "/services/interactive-experiences",
            headerProps: { light: true },
          },
          {
            label: "Website Security",
            icon: <IconShieldCheck size={24} />,
            href: "/services/website-security",
            headerProps: { light: true },
          },
        ],
      },
    },
    {
      label: "Portfolio",
      href: "/portfolio",
    },
    {
      label: "Blog",
      href: "/blog",
    },
  ],
  cta: {
    label: "Contact Us",
    href: "#",
    icon: <IconMessage />,
  },
};

export function Layout(props: LayoutProps) {
  const { children, headerConfig = {} } = props;
  const location = useLocation();
  const isInitialMount = React.useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFading, setIsFading] = useState(false);

  // Get header props based on current path
  const getHeaderProps = () => {
    const currentPath = location.pathname;
    // Normalize path by removing trailing slash if present (except for root)
    const normalizedPath =
      currentPath === "/" ? "/" : currentPath.replace(/\/$/, "");

    // Check main menu items
    for (const item of menuStructure.items) {
      const itemPath = item.href === "/" ? "/" : item.href?.replace(/\/$/, "");
      if (itemPath === normalizedPath && item.headerProps) {
        return item.headerProps;
      }

      // Check submenu items
      if (item.submenu) {
        for (const link of item.submenu.links) {
          const linkPath =
            link.href === "/" ? "/" : link.href?.replace(/\/$/, "");
          if (linkPath === normalizedPath && link.headerProps) {
            return link.headerProps;
          }
        }
      }
    }

    return {}; // Default to no additional props
  };

  // Merge navigation-based props with page-specific config
  // Page-specific config takes precedence
  const navigationProps = getHeaderProps();
  const headerProps = { ...navigationProps, ...headerConfig };

  // Show loading state when pathname changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setIsLoading(true);
    setIsFading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
      setIsFading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <BreakpointProvider>
      {/* Loading bar */}
      <Box
        pos="fixed"
        mx="auto"
        maw={1870}
        top={{ base: 16, md: 29 }}
        left={{ base: 17, md: 30 }}
        right={{ base: 17, md: 30 }}
        h={3}
        bg="brandPrimary"
        style={{
          zIndex: 9999,
          opacity: isLoading ? 1 : 0,
          transform: isLoading ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left",
          transition: isLoading
            ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            : "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRadius: "var(--mantine-radius-xs)",
        }}
      />

      <Container size={1920} p="xs">
        <MobileHeader
          hiddenFrom="md"
          menuStructure={menuStructure}
          {...headerProps}
        />
        <Header
          visibleFrom="md"
          menuStructure={menuStructure}
          {...headerProps}
        />

        <Box pos="relative">
          {children}

          {/* Dark overlay during transition */}
          <Box
            pos="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.3)"
            style={{
              opacity: isFading ? 1 : 0,
              transition: "opacity 0.2s ease-out",
              pointerEvents: isFading ? "auto" : "none",
              zIndex: 1000,
            }}
          />
        </Box>

        <LastUpdated />

        <Footer />
      </Container>
      <CookieBanner />
    </BreakpointProvider>
  );
}
